import * as Sentry from '@sentry/browser';
import React, { FC, useState } from 'react';
import { UpdateReferralStatus } from '../../../../GraphQL';
import { Button, Form, Input, message, Modal, Select } from 'antd';
import { deletePaginatedCacheItems } from '../../../../helpers/cache';
import { useMutation } from '@apollo/client';
import { ReferralData, QUERY } from './GetReferralQuery';
import { useAppContext } from '../../../../hooks';
import { LIST_AUDIT_LOG_QUERY } from './ListAuditLogQuery';
import { DEFAULT_REJECTION_REASONS } from './rejectionReasons';

interface UpdateReferralStatusMutationVars {
  referralStatusInput: {
    id: string;
    status: string;
    rejectReason: string;
    rejectNote: string;
  };
}

interface UpdateReferralStatusMutationData {
  UpdateReferralStatus: {
    id: string;
    status: string;
  };
}

interface RejectReferralProps {
  referral?: ReferralData;
  onComplete: () => void;
  isOpen?: boolean;
  onClose?: () => void;
}

export const RejectReferral: FC<RejectReferralProps> = ({
  referral,
  onComplete,
  isOpen,
  onClose,
}): JSX.Element | null => {
  const { currentOrganization } = useAppContext();

  const useDeclineLabel = currentOrganization?.referralSettings?.useDeclineReferralLabel;
  const rejectLabel = useDeclineLabel ? 'Decline' : 'Reject';

  const orgReasons = currentOrganization?.referralRejectionReasons;
  const reasons = orgReasons?.length ? orgReasons : DEFAULT_REJECTION_REASONS;
  const defaultReason = reasons[0].value || reasons[0].key;

  const showOwnButton = isOpen === undefined;
  const [visible, setVisible] = useState<boolean>(false);
  const [reason, setReason] = useState<string>(defaultReason);
  const [reasonText, setReasonText] = useState<string | null>(null);

  const modalVisible = showOwnButton ? visible : !!isOpen;
  const closeModal = showOwnButton ? () => setVisible(false) : () => onClose?.();

  const hasActiveAppointment = referral?.appointment && !['cancelled', 'failed'].includes(referral.appointment.status);

  const [updateReferralStatusMutation, { error }] = useMutation<
    UpdateReferralStatusMutationData,
    UpdateReferralStatusMutationVars
  >(UpdateReferralStatus);

  if (error) {
    Sentry.captureException(error);
    message.error(`Error ${useDeclineLabel ? 'declining' : 'rejecting'} referral`);
  }

  const nextStatuses = referral?.nextStatuses as Array<string>;
  if (!referral || (showOwnButton && !nextStatuses.includes('rejected'))) {
    return null;
  }

  const onOk = (): void => {
    closeModal();

    updateReferralStatusMutation({
      variables: {
        referralStatusInput: {
          id: referral.id,
          status: 'rejected',
          rejectReason: reason,
          rejectNote: reasonText || reason,
        },
      },
      refetchQueries: [
        {
          query: QUERY,
          variables: {
            id: referral.id,
            organizationId: currentOrganization?.id as string,
          },
        },
        {
          query: LIST_AUDIT_LOG_QUERY,
          variables: {
            action: 'referral.status.updated',
            recordId: referral?.id,
            organizationId: currentOrganization?.id as string,
          },
        },
      ],
      update: cache => {
        message.success(`Referral has been ${useDeclineLabel ? 'declined' : 'rejected'}`);
        deletePaginatedCacheItems(cache, 'ListReferrals');
        deletePaginatedCacheItems(cache, 'ListAppointments');
        onComplete();
      },
    });
  };

  return (
    <>
      {showOwnButton && (
        <Button className="mt-1" onClick={() => setVisible(true)}>
          {rejectLabel} Referral
        </Button>
      )}
      <Modal title={`${rejectLabel} Referral`} visible={modalVisible} onOk={() => onOk()} onCancel={closeModal}>
        {hasActiveAppointment && (
          <p style={{ marginBottom: 16, color: '#faad14' }}>
            <strong>Note:</strong> This will also cancel the scheduled appointment.
          </p>
        )}
        <Form>
          <Form.Item label={`Reason for ${useDeclineLabel ? 'declining' : 'rejection'}`}>
            <Select<string>
              placeholder="Choose reason"
              defaultValue={defaultReason}
              size="small"
              onChange={val => {
                setReason(val);
                setReasonText(null);
              }}
            >
              {reasons.map(r => (
                <Select.Option key={r.key} value={r.value || r.key}>
                  {r.key}
                </Select.Option>
              ))}
            </Select>
            {reason === 'other' && (
              <Input
                maxLength={128}
                onChange={e => setReasonText(e.target.value)}
                placeholder="Additional detail"
                size="small"
              ></Input>
            )}
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};
